import React, { useState, useEffect, useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment-timezone';
import ExcelJS from 'exceljs';
import { BiDownload, BiBarChartAlt2, BiTable } from 'react-icons/bi';

import {
    TemplateField, TemplateResponseDto, TemplateResponsePagination, CHOICE_TYPES, FieldType,
} from '../../types/template';

// ── Helpers ────────────────────────────────────────────────────────────────────

function parseAnswers(json: string | null | undefined): Record<string, unknown> {
    if (!json) return {};
    try { return JSON.parse(json); } catch { return {}; }
}

function colLabel(field: TemplateField): string {
    const sep = field.label.indexOf(' · ');
    return sep > -1 ? field.label.slice(sep + 3) : field.label;
}

const DAY_ABBR: Record<string, string> = {
    Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed',
    Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};
function shortLabel(field: TemplateField): string {
    const label = colLabel(field);
    // Check the raw label too — standalone fields may have no " · " but still end with a day name
    const candidates = [label, field.label];
    for (const s of candidates) {
        const day = Object.keys(DAY_ABBR).find(d => s === d || s.endsWith(' ' + d) || s.endsWith(' · ' + d));
        if (day) return DAY_ABBR[day];
    }
    return label.length > 10 ? label.slice(0, 10) + '…' : label;
}

function displayValue(field: TemplateField, answers: Record<string, unknown>): string {
    const val = answers[field.id];
    if (val === undefined || val === null || val === '') return '-';
    if (CHOICE_TYPES.includes(field.type)) {
        const opts = field.options ?? [];
        if (field.type === 'single_choice') return opts.find(o => o.id === val)?.label ?? String(val);
        if (Array.isArray(val))
            return (val as string[]).map(id => opts.find(o => o.id === id)?.label ?? id).join(', ') || '-';
        return opts.find(o => o.id === val)?.label ?? String(val);
    }
    if (field.type === 'star_rating') return `${val} ★`;
    return String(val);
}

// ── Value pill ─────────────────────────────────────────────────────────────────
function ValuePill({ value, fieldType }: { value: string; fieldType?: FieldType }) {
    if (value === '-') return <span style={{ color: '#ced4da', fontSize: 12 }}>—</span>;

    // Image upload — show thumbnail if URL, else icon
    if (fieldType === 'image_upload') {
        const isUrl = value.startsWith('http') || value.startsWith('/');
        if (isUrl) return (
            <a href={value} target="_blank" rel="noreferrer">
                <img src={value} alt="upload" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6' }} />
            </a>
        );
        return <span style={{ fontSize: 11, color: '#6c757d' }}>📎 {value.length > 14 ? value.slice(0, 14) + '…' : value}</span>;
    }

    // Comment box — truncate long text with tooltip
    if (fieldType === 'comment_box' && value.length > 30) {
        return <span title={value} style={{ fontSize: 12, color: '#495057', cursor: 'help' }}>{value.slice(0, 30)}…</span>;
    }

    const lc = value.toLowerCase();
    if (lc === 'yes')
        return <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>Yes</span>;
    if (lc === 'no')
        return <span style={{ background: '#f1f3f5', color: '#868e96', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>No</span>;
    if (lc.includes('vegan'))
        return <span style={{ background: '#ecfccb', color: '#3f6212', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>Vegan</span>;
    if (value.includes('★'))
        return <span style={{ color: '#f59e0b', fontSize: 12, fontWeight: 600 }}>{value}</span>;

    // Multi-choice answers (comma-separated) — clamp width
    if (value.length > 24)
        return <span title={value} style={{ fontSize: 12, color: '#495057', cursor: 'help' }}>{value.slice(0, 24)}…</span>;

    return <span style={{ fontSize: 12 }}>{value}</span>;
}

// ── Two-level header groups ────────────────────────────────────────────────────
type HeaderGroup = { prefix: string | null; fields: TemplateField[] };
function buildHeaderGroups(fields: TemplateField[]): HeaderGroup[] {
    const groups: HeaderGroup[] = [];
    for (const f of fields) {
        const sep = f.label.indexOf(' · ');
        if (sep > -1) {
            const prefix = f.label.slice(0, sep);
            const last = groups[groups.length - 1];
            if (last?.prefix === prefix) last.fields.push(f);
            else groups.push({ prefix, fields: [f] });
        } else {
            groups.push({ prefix: null, fields: [f] });
        }
    }
    return groups;
}

type Section = { prefix: string | null; fields: TemplateField[] };
function groupFields(fields: TemplateField[]): Section[] {
    const out: Section[] = [];
    for (const f of fields) {
        const sep = f.label.indexOf(' · ');
        if (sep > -1) {
            const prefix = f.label.slice(0, sep);
            const last = out[out.length - 1];
            if (last?.prefix === prefix) last.fields.push(f);
            else out.push({ prefix, fields: [f] });
        } else {
            out.push({ prefix: null, fields: [f] });
        }
    }
    return out;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function OptionBar({ label, count, total }: { label: string; count: number; total: number }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                <span style={{ fontWeight: 500 }}>{label}</span>
                <span style={{ color: '#6c757d' }}>{count} <span style={{ fontSize: 11 }}>({pct}%)</span></span>
            </div>
            <div style={{ height: 7, background: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#c5281f', borderRadius: 4, transition: 'width .4s ease' }} />
            </div>
        </div>
    );
}

function FieldCard({ field, answers }: { field: TemplateField; answers: Record<string, unknown>[] }) {
    const total    = answers.length;
    const answered = answers.filter(a => {
        const v = a[field.id];
        return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && (v as unknown[]).length === 0);
    }).length;

    if (CHOICE_TYPES.includes(field.type) && (field.options?.length ?? 0) > 0) {
        const opts = field.options ?? [];
        const counts = opts.map(opt => ({
            ...opt,
            count: answers.filter(a => {
                const v = a[field.id];
                return field.type === 'single_choice' ? v === opt.id : Array.isArray(v) && (v as string[]).includes(opt.id);
            }).length,
        }));
        return (
            <div className="form-report-field-card">
                <div className="form-report-field-card__label">{colLabel(field)}</div>
                <div className="form-report-field-card__answered">{answered}/{total} answered</div>
                <div style={{ marginTop: 8 }}>
                    {counts.map(o => <OptionBar key={o.id} label={o.label} count={o.count} total={answered} />)}
                </div>
            </div>
        );
    }

    if (field.type === 'star_rating') {
        const ratings = answers.map(a => a[field.id] as number).filter(v => typeof v === 'number' && v > 0);
        const avg = ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : '-';
        return (
            <div className="form-report-field-card">
                <div className="form-report-field-card__label">{colLabel(field)}</div>
                <div className="form-report-field-card__answered">{answered}/{total} answered</div>
                <div style={{ marginTop: 10, fontSize: 28, fontWeight: 700, color: '#c5281f' }}>
                    {avg} <span style={{ fontSize: 14, color: '#6c757d', fontWeight: 400 }}>/ {field.maxStars ?? 5} avg</span>
                </div>
            </div>
        );
    }

    const texts = answers.map(a => String(a[field.id] ?? '')).filter(v => v && v !== 'undefined');

    if (field.type === 'image_upload') {
        return (
            <div className="form-report-field-card">
                <div className="form-report-field-card__label">{colLabel(field)}</div>
                <div className="form-report-field-card__answered">{answered}/{total} uploaded</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {texts.slice(0, 12).map((url, i) => (
                        url.startsWith('http') || url.startsWith('/') ? (
                            <a key={i} href={url} target="_blank" rel="noreferrer">
                                <img src={url} alt={`upload-${i}`}
                                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }} />
                            </a>
                        ) : (
                            <span key={i} style={{ fontSize: 11, color: '#6c757d', background: '#f8f9fa', borderRadius: 4, padding: '2px 6px' }}>📎 {url.slice(0, 16)}</span>
                        )
                    ))}
                    {texts.length > 12 && <div style={{ fontSize: 11, color: '#adb5bd', marginTop: 4 }}>+{texts.length - 12} more</div>}
                    {texts.length === 0 && <div style={{ fontSize: 12, color: '#adb5bd', fontStyle: 'italic' }}>No uploads yet</div>}
                </div>
            </div>
        );
    }

    return (
        <div className="form-report-field-card">
            <div className="form-report-field-card__label">{colLabel(field)}</div>
            <div className="form-report-field-card__answered">{answered}/{total} answered</div>
            <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto' }}>
                {texts.slice(0, 10).map((t, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: '#495057', padding: '3px 0', borderBottom: '1px solid #f4f4f4' }}>{t}</div>
                ))}
                {texts.length > 10 && <div style={{ fontSize: 11, color: '#adb5bd', marginTop: 4 }}>+{texts.length - 10} more</div>}
                {texts.length === 0 && <div style={{ fontSize: 12, color: '#adb5bd', fontStyle: 'italic' }}>No text answers yet</div>}
            </div>
        </div>
    );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

interface Props {
    token:        string;
    templateCode: string;
    formName:     string;
    fields:       TemplateField[];
}

type SubTab = 'summary' | 'responses';

export default function FormReportPanel({ token, templateCode, formName, fields }: Props) {
    const api = process.env.REACT_APP_API_END_POINT;

    const [responses,    setResponses]    = useState<TemplateResponseDto[]>([]);
    const [loading,      setLoading]      = useState(false);
    const [subTab,       setSubTab]       = useState<SubTab>('summary');
    const [exporting,    setExporting]    = useState(false);

    const sortedFields = useMemo(() => fields.slice().sort((a, b) => a.order - b.order), [fields]);
    const sections     = useMemo(() => groupFields(sortedFields), [sortedFields]);

    const thStyle: React.CSSProperties = {
        padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600, fontSize: 11,
        verticalAlign: 'middle', border: '1px solid #6c757d',
    };
    const tdStyle: React.CSSProperties = {
        padding: '4px 8px', verticalAlign: 'middle', border: '1px solid #dee2e6',
    };
    const stickyNumStyle: React.CSSProperties = {
        ...thStyle, position: 'sticky', left: 0, zIndex: 3, background: '#f1f3f5',
    };
    const stickyIdStyle: React.CSSProperties = {
        ...thStyle, position: 'sticky', left: 36, zIndex: 3, minWidth: 80,
        background: '#f1f3f5', boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
    };
    const stickyNumTdStyle: React.CSSProperties = {
        ...tdStyle, position: 'sticky', left: 0, zIndex: 1, background: 'inherit',
    };
    const stickyIdTdStyle: React.CSSProperties = {
        ...tdStyle, position: 'sticky', left: 36, zIndex: 1, background: 'inherit',
        boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
    };

    // Detect if all responses are anonymous (employeeId === null)
    const isAnonymous = useMemo(() => responses.every(r => !r.employeeId), [responses]);

    // Find Employee ID field in form schema (to use as display ID for anonymous)
    const empIdField = useMemo(() =>
        sortedFields.find(f => /employee[\s_-]?id|รหัสพนักงาน|emp[\s_-]?id/i.test(f.label)),
        [sortedFields]
    );

    // Table columns = all fields except empIdField when anonymous (avoid duplicate)
    const tableFields = useMemo(() =>
        (isAnonymous && empIdField) ? sortedFields.filter(f => f.id !== empIdField.id) : sortedFields,
        [sortedFields, isAnonymous, empIdField]
    );

    const headerGroups = useMemo(() => buildHeaderGroups(tableFields), [tableFields]);

    const parsedAnswers = useMemo(
        () => responses.map(r => parseAnswers(r.answersJson)),
        [responses]
    );

    useEffect(() => {
        if (!token || !templateCode) return;
        setLoading(true);
        axios.get<TemplateResponsePagination>(`${api}/v1/Form/responses`, {
            headers: { Authorization: `Bearer ${token}` },
            params:  { code: templateCode, offset: 0, limit: 2000 },
        })
            .then(res => setResponses(res.data.responses))
            .catch(() => toast.error('Failed to load responses'))
            .finally(() => setLoading(false));
    }, [token, templateCode]); // eslint-disable-line

    // ── Export Excel ────────────────────────────────────────────────────────────
    const handleExportExcel = async () => {
        if (!responses.length) { toast.warn('No responses to export'); return; }
        setExporting(true);
        try {
            const wb = new ExcelJS.Workbook();
            wb.creator = 'HR Events System';

            const ws = wb.addWorksheet('Responses');
            ws.columns = [
                { header: '#',           key: 'no',      width: 6  },
                { header: 'Employee ID', key: 'empId',   width: 16 },
                { header: 'Name',        key: 'empName', width: 24 },
                ...sortedFields.map(f => ({ header: colLabel(f), key: f.id, width: 22 })),
                { header: 'Submitted',   key: 'submitted', width: 20 },
            ];
            const hdr = ws.getRow(1);
            hdr.height = 26;
            hdr.eachCell(cell => {
                cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC5281F' } };
                cell.font      = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11, name: 'Calibri' };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border    = { bottom: { style: 'thin', color: { argb: 'FF9B1C18' } } };
            });
            responses.forEach((r, i) => {
                const ans: Record<string, unknown> = {
                    no: i + 1, empId: r.employeeId ?? '-', empName: r.employeeName ?? '-',
                    submitted: r.submittedAt ? moment.utc(r.submittedAt).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm') : '-',
                };
                sortedFields.forEach(f => { ans[f.id] = displayValue(f, parsedAnswers[i]); });
                const row = ws.addRow(ans);
                row.height = 20;
                row.eachCell(cell => {
                    cell.alignment = { vertical: 'middle' };
                    cell.font = { name: 'Calibri', size: 11 };
                    if ((i + 1) % 2 === 0)
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F6F8' } };
                });
            });
            ws.views = [{ state: 'frozen', ySplit: 1 }];

            const buf  = await wb.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = `${formName}_responses.xlsx`; a.click();
            URL.revokeObjectURL(url);
            toast.success('Downloaded!');
        } catch { toast.error('Export failed'); }
        finally { setExporting(false); }
    };

    if (!templateCode) {
        return <div className="text-center text-muted py-5">Save the form first to view reports.</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Sub-header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderBottom: '1px solid #e9ecef', background: '#fff', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: 4 }}>
                    <button
                        onClick={() => setSubTab('summary')}
                        className={`btn btn-sm ${subTab === 'summary' ? 'btn-danger' : 'btn-outline-secondary'}`}
                    >
                        <BiBarChartAlt2 className="me-1" />Summary
                    </button>
                    <button
                        onClick={() => setSubTab('responses')}
                        className={`btn btn-sm ${subTab === 'responses' ? 'btn-danger' : 'btn-outline-secondary'}`}
                    >
                        <BiTable className="me-1" />Responses {loading ? '' : `(${responses.length})`}
                    </button>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {loading && <Spinner size="sm" />}
                    {!loading && <span className="badge bg-secondary">{responses.length} responses</span>}
                    <Button
                        size="sm" variant="outline-success"
                        disabled={!responses.length || exporting}
                        onClick={handleExportExcel}
                    >
                        {exporting ? <Spinner size="sm" className="me-1" /> : <BiDownload className="me-1" />}
                        Export Excel
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                {loading ? (
                    <div className="text-center py-5"><Spinner /></div>
                ) : responses.length === 0 ? (
                    <div className="text-center text-muted py-5">No responses yet.</div>
                ) : subTab === 'summary' ? (
                    <>
                        {sortedFields.length === 0
                            ? <div className="text-center text-muted py-5">This form has no fields.</div>
                            : sections.map((sec, si) =>
                                sec.prefix !== null ? (
                                    <div key={`s-${si}`} className="form-report-section mb-4">
                                        <div className="form-report-section__header">{sec.prefix}</div>
                                        <div className="form-report-section__grid">
                                            {sec.fields.map(f => <FieldCard key={f.id} field={f} answers={parsedAnswers} />)}
                                        </div>
                                    </div>
                                ) : sec.fields.map(f => (
                                    <div key={f.id} className="mb-3">
                                        <FieldCard field={f} answers={parsedAnswers} />
                                    </div>
                                ))
                            )
                        }
                    </>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table table-sm table-hover bg-white shadow-sm" style={{ minWidth: 400, fontSize: 12, borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                {/* Row 1 — group / meta headers */}
                                <tr style={{ background: '#f1f3f5', color: '#212529' }}>
                                    <th rowSpan={2} style={stickyNumStyle}>#</th>
                                    <th rowSpan={2} style={stickyIdStyle}>
                                        {isAnonymous ? 'ID (form)' : 'Employee ID'}
                                    </th>
                                    {!isAnonymous && (
                                        <th rowSpan={2} style={{ ...thStyle, minWidth: 120, background: '#f1f3f5' }}>Name</th>
                                    )}
                                    {headerGroups.map((g, gi) =>
                                        g.prefix !== null ? (
                                            <th key={`g-${gi}`}
                                                colSpan={g.fields.length}
                                                style={{ ...thStyle, background: '#e9ecef', textAlign: 'center', borderLeft: '2px solid #ced4da', color: '#495057' }}
                                                title={g.prefix}
                                            >
                                                {g.prefix.length > 16 ? g.prefix.slice(0, 16) + '…' : g.prefix}
                                            </th>
                                        ) : (
                                            <th key={`g-${gi}`} rowSpan={2} style={{ ...thStyle, minWidth: 80, background: '#f1f3f5' }}
                                                title={g.fields[0].label}>
                                                {shortLabel(g.fields[0])}
                                            </th>
                                        )
                                    )}
                                    <th rowSpan={2} style={{ ...thStyle, minWidth: 110, background: '#f1f3f5' }}>Submitted</th>
                                </tr>
                                {/* Row 2 — field sub-headers (grouped only) */}
                                <tr style={{ background: '#e9ecef', color: '#495057' }}>
                                    {headerGroups.filter(g => g.prefix !== null).flatMap(g =>
                                        g.fields.map(f => (
                                            <th key={f.id} title={f.label}
                                                style={{ ...thStyle, background: '#e9ecef', fontWeight: 500, borderLeft: '1px solid #ced4da', minWidth: 46, textAlign: 'center' }}>
                                                {shortLabel(f)}
                                            </th>
                                        ))
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {responses.map((r, i) => {
                                    const ans = parsedAnswers[i];
                                    const displayId = isAnonymous && empIdField
                                        ? (displayValue(empIdField, ans) !== '-' ? displayValue(empIdField, ans) : `#${i + 1}`)
                                        : (r.employeeId ?? '-');
                                    return (
                                        <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                                            <td style={stickyNumTdStyle} className="text-muted">{i + 1}</td>
                                            <td style={{ ...stickyIdTdStyle, fontFamily: 'monospace', fontWeight: 600, color: '#212529' }}>{displayId}</td>
                                            {!isAnonymous && (
                                                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{r.employeeName ?? '-'}</td>
                                            )}
                                            {tableFields.map(f => (
                                                <td key={f.id} style={{ ...tdStyle, textAlign: 'center' }}>
                                                    <ValuePill value={displayValue(f, ans)} fieldType={f.type} />
                                                </td>
                                            ))}
                                            <td style={{ ...tdStyle, color: '#6c757d', fontSize: 11, whiteSpace: 'nowrap' }}>
                                                {r.submittedAt ? moment.utc(r.submittedAt).tz('Asia/Bangkok').format('DD MMM YY HH:mm') : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="text-muted small mt-2">Total: {responses.length} responses</div>
                    </div>
                )}
            </div>
        </div>
    );
}
